<template>
  <div v-if="isVisible" class="jobs-placeholder">
    <div class="jobs-placeholder__info">
      <h1 class="jobs-placeholder__title">
        Sales jobs that
        <br />
        don’t suck.
      </h1>
      <p class="jobs-placeholder__description">
        {{ description }}
      </p>
      <div class="jobs-placeholder__action">
        <BravadoButtonV3 class="jobs-placeholder__button" data-test="apply-button" elevated @click="handleApply">
          {{ buttonTitle }}
        </BravadoButtonV3>
      </div>
    </div>
    <div class="jobs-placeholder__benefits">
      <div v-for="benefit in benefits" :key="benefit.id" class="jobs-placeholder__benefit">
        <Component :is="benefit.icon" />
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p class="jobs-placeholder__text" v-html="benefit.label"></p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from '@nuxtjs/composition-api';
import JobsPlaceholderCurrentJobsInquiryQuery from './JobsPlaceholderCurrentJobsInquiry.query';
import BillIcon from './images/bill.vue.svg';
import CheckIcon from './images/check.vue.svg';
import PieIcon from './images/pie.vue.svg';
import VideoIcon from './images/video.vue.svg';
import BravadoButtonV3 from '@/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import { JobsInquiryState } from '@/types/graphql';
import type { JobsInquiry } from '@/types/graphql';
import { getOnboardingV2Steps } from '@/components/partials/jobs/onboarding/utils';
import type { JobsOnboardingStep } from '@/components/partials/jobs/onboarding/types';
import { getPortfolioPath } from '@/utils/routes';

export default defineComponent({
  name: 'JobsPlaceholder',
  components: { BravadoButtonV3, BillIcon, CheckIcon, PieIcon, VideoIcon },
  apollo: {
    currentJobsInquiry: {
      query: JobsPlaceholderCurrentJobsInquiryQuery,
      skip(): boolean {
        return !this.$auth.loggedIn;
      },
      error(e): void {
        this.$notice.error(e);
      },
    },
  },
  data(): { redirected: boolean; currentJobsInquiry?: JobsInquiry } {
    return {
      redirected: false,
      currentJobsInquiry: undefined,
    };
  },
  computed: {
    benefits() {
      return [
        {
          id: 'bonus',
          icon: 'BillIcon',
          label: `<strong>$100 success fee</strong> if you get hired through us`,
        },
        {
          id: 'check',
          icon: 'CheckIcon',
          label: '<strong>Vetted, fast-growing</strong> companies where reps are hitting quota',
        },
        {
          id: 'pie',
          icon: 'PieIcon',
          label: '<strong>Realistic quotas</strong> and <strong>OTEs</strong> you can hit and exceed',
        },
        { id: 'video', icon: 'VideoIcon', label: 'Interview <strong>coaching</strong> from top sales leaders' },
      ];
    },
    steps(): JobsOnboardingStep[] | undefined {
      return getOnboardingV2Steps();
    },
    step(): JobsOnboardingStep | undefined {
      return this.steps?.find(step => step.key === this.currentJobsInquiry?.onboardingStateV2);
    },
    progress(): number {
      if (!this.steps) return 0;

      if (!this.step) {
        return 0;
      }

      const index = this.steps.findIndex(step => step.key === this.currentJobsInquiry?.onboardingStateV2);
      return Math.round((index / this.steps.length) * 100);
    },
    description(): string {
      if (this.isFilledOnboarding) {
        return 'You successfully filled out your application!';
      }

      if (this.progress > 0) {
        return `Your application is ${this.progress}% done! Continue anytime.`;
      }

      return 'Salespeople get screwed all the time on empty promises. We are changing how sales hiring is done.';
    },
    buttonTitle(): string {
      if (this.isFilledOnboarding) {
        return 'Open Profile';
      }

      if (this.progress > 0) {
        return 'Continue the survey';
      }

      return 'Apply Now';
    },
    isVisible(): boolean {
      return !this.$auth.loggedIn || this.currentJobsInquiry !== undefined;
    },
    isFilledOnboarding(): boolean {
      if (!this.currentJobsInquiry) return false;

      return this.currentJobsInquiry.state !== JobsInquiryState.Initialized;
    },
  },
  methods: {
    async handleApply(): Promise<void> {
      if (this.redirected) {
        return;
      }

      if (!this.$auth.loggedIn) {
        return this.$modal.showWithRoute(
          { custom: this.$constants.dynamicModals.WAR_ROOM_SIGN_UP_MODAL },
          { refferer: '/jobs' },
        );
      }

      this.redirected = true;

      if (this.isFilledOnboarding) {
        await this.$router.push(getPortfolioPath(this.$auth.user.slug!));
        return;
      }

      this.$analytics.logEvent('jobs__welcome__survey__start', { continue: !!this.currentJobsInquiry });

      window.location.href = `/simplified-onboarding/?toLastStep=true`;
    },
  },
});
</script>

<style scoped lang="scss">
.jobs-placeholder {
  box-shadow: var(--theme-elevation-01);
  background: var(--theme-color-card);
  padding: 32px 16px;
  border-radius: 8px;

  @include mq('sm') {
    padding: 24px 48px 24px 24px;
    display: flex;
    align-items: center;
  }

  &__info {
    text-align: center;

    @include mq('sm') {
      text-align: left;
      padding: 24px 48px 24px 24px;
      flex: 1;
    }
  }

  &__title {
    margin: 0;

    @include header2;

    background: var(--theme-cyan_hc_gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    @include mq('sm') {
      @include header1;
    }
  }

  &__description {
    @include body;

    color: var(--theme-color-text);
    margin-top: 24px;
    padding: 0 20px;

    @include mq('sm') {
      @include headline-semibold;

      padding: 0;
    }
  }

  &__action {
    display: flex;
    flex-direction: column;
    margin-top: 24px;
    align-items: center;

    @include mq('sm') {
      flex-direction: row;
    }
  }

  &__button {
    min-width: 150px;
  }

  &__hint {
    color: var(--gray-50);
    margin-top: 16px;

    @include mq('sm') {
      margin-top: 0;
      margin-left: 16px;
      max-width: 150px;
    }
  }

  &__text {
    @include body;

    color: var(--theme-color-text);
    margin-top: 8px;
  }

  &__benefits {
    margin-top: 24px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 16px;

    @include mq('sm') {
      margin-top: 0;
      flex: 0 0 42%;
      padding: 14px 0;
    }
  }

  &__benefit {
    padding: 16px;
    background: var(--theme-color-background);
    border-radius: 16px;
    text-align: center;
  }
}
</style>
